import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from 'uuid';

export interface ProfileData {
  id: string;
  nickname: string;
  preferred_game: string;
  slug?: string | null;
  biography: string | null;
  youtube_url: string | null;
  instagram_url: string | null;
  discord_url: string | null;
  twitch_url: string | null;
}

export interface MentorInfoData extends ProfileData {
  achievements: Array<{
    id: string;
    title: string;
    description?: string;
  }> | null;
  image_url?: string | null;
  cover_url?: string | null;
  video_url?: string | null; // 🎯 Campo para vídeo da página pública
}

// Função para carregar dados do perfil básico
export const fetchProfileData = async (userId: string) => {
  const { data: profile, error } = await supabase
    .from("profiles")
    .select("id, nickname, preferred_game, slug, email, role")
    .eq("id", userId)
    .single();

  if (error) throw error;
  return profile;
};

// Função para atualizar dados básicos do perfil
export const updateProfileData = async (userId: string, updates: {
  nickname?: string;
  preferred_game?: string;
}) => {
  const { data, error } = await supabase
    .from("profiles")
    .update(updates)
    .eq("id", userId)
    .select();

  if (error) throw error;
  return data;
};

// Função para carregar dados do mentor
export const fetchMentorData = async (userId: string) => {
  // Primeiro, busca o mentor_info pelo profile_id
  const { data: mentorInfo, error: mentorError } = await supabase
    .from("mentor_info")
    .select("*")
    .eq("profile_id", userId)
    .single();

  if (mentorError) throw mentorError;

  // Estrutura os dados sociais no formato esperado pelo frontend
  const formattedData = {
    ...mentorInfo,
    social: {
      youtube: mentorInfo.youtube_url,
      instagram: mentorInfo.instagram_url,
      discord: mentorInfo.discord_url,
      twitch: mentorInfo.twitch_url
    }
  };

  return formattedData;
};

// Função para atualizar dados do mentor
export const updateMentorData = async (userId: string, updates: {
  bio?: string | null;
  biography?: string | null;
  youtube_url?: string | null;
  instagram_url?: string | null;
  discord_url?: string | null;
  twitch_url?: string | null;
  video_url?: string | null; // 🎯 Campo para vídeo da página pública
  achievements?: any | null;
  image_url?: string | null;
  cover_url?: string | null;
}) => {
  console.log('🔄 [updateMentorData] Atualizando dados do mentor:', userId);
  console.log('📝 [updateMentorData] Dados para atualização:', JSON.stringify(updates, null, 2));
  
  // PRIMEIRO: Verificar se existe registro no mentor_info
  console.log('🔍 [updateMentorData] Verificando se existe registro para profile_id:', userId);
  const { data: existingRecord, error: checkError } = await supabase
    .from("mentor_info")
    .select("id, profile_id")
    .eq("profile_id", userId)
    .maybeSingle();

  if (checkError) {
    console.error('❌ [updateMentorData] Erro ao verificar registro existente:', JSON.stringify(checkError, null, 2));
    throw checkError;
  }

  console.log('🔍 [updateMentorData] Registro existente encontrado:', JSON.stringify(existingRecord, null, 2));

  // Prepara os dados para atualização
  const bioValue = updates.biography || updates.bio;
  const updateData = {
    bio: bioValue,
    biography: bioValue,  // Atualiza ambos os campos para manter sincronizado
    youtube_url: updates.youtube_url,
    instagram_url: updates.instagram_url,
    discord_url: updates.discord_url,
    twitch_url: updates.twitch_url,
    video_url: updates.video_url, // 🎯 Campo para vídeo da página pública
    achievements: updates.achievements,
    image_url: updates.image_url,
    cover_url: updates.cover_url
  };

  console.log('📦 [updateMentorData] Dados formatados para envio:', JSON.stringify(updateData, null, 2));

  if (!existingRecord) {
    // SE NÃO EXISTE: Criar novo registro
    console.log('🆕 [updateMentorData] Registro não existe, criando novo...');
    const { data, error } = await supabase
      .from("mentor_info")
      .insert({
        profile_id: userId,
        ...updateData
      })
      .select();

    if (error) {
      console.error('❌ [updateMentorData] Erro ao criar registro:', JSON.stringify(error, null, 2));
      throw error;
    }

    console.log('✅ [updateMentorData] Novo registro criado com sucesso:', JSON.stringify(data, null, 2));
    return data;
  } else {
    // SE EXISTE: Atualizar registro existente
    console.log('🔄 [updateMentorData] Registro existe, atualizando...');
    const { data, error } = await supabase
      .from("mentor_info")
      .update(updateData)
      .eq("profile_id", userId)
      .select();

    if (error) {
      console.error('❌ [updateMentorData] Erro ao atualizar:', JSON.stringify(error, null, 2));
      throw error;
    }

    console.log('📊 [updateMentorData] Resultado da atualização - linhas afetadas:', data?.length || 0);
    console.log('✅ [updateMentorData] Dados atualizados:', JSON.stringify(data, null, 2));
    
    if (!data || data.length === 0) {
      console.warn('⚠️ [updateMentorData] AVISO: Nenhuma linha foi atualizada! Isso pode indicar que os dados já estavam iguais ou há problema de permissão.');
      
      // Fazer uma busca para verificar se os dados foram realmente salvos
      const { data: verifyData, error: verifyError } = await supabase
        .from("mentor_info")
        .select("*")
        .eq("profile_id", userId)
        .single();
        
      if (verifyError) {
        console.error('❌ [updateMentorData] Erro ao verificar dados salvos:', JSON.stringify(verifyError, null, 2));
      } else {
        console.log('🔍 [updateMentorData] Dados atuais no banco após update:', JSON.stringify(verifyData, null, 2));
      }
    }

    return data;
  }
};

// Função para carregar dados sociais do jogador
export const fetchPlayerSocialData = async (userId: string) => {
  // Verifica se o jogador já tem dados sociais
  const { data: playerSocial, error } = await supabase
    .from("player_social_media")
    .select("*")
    .eq("profile_id", userId)
    .maybeSingle();
    
  if (error) throw error;
  
  // Se não encontrou, cria um registro vazio
  if (!playerSocial) {
    const { data: newRecord, error: insertError } = await supabase
      .from("player_social_media")
      .insert({ profile_id: userId })
      .select();
      
    if (insertError) throw insertError;
    return newRecord[0];
  }
  
  return playerSocial;
};

// Função para atualizar dados sociais do jogador
export const updatePlayerSocialData = async (userId: string, updates: {
  biography?: string | null;
  youtube_url?: string | null;
  instagram_url?: string | null;
  discord_url?: string | null;
  twitch_url?: string | null;
}) => {
  // Verifica se o jogador já tem um registro
  const { data: existing } = await supabase
    .from("player_social_media")
    .select("id")
    .eq("profile_id", userId)
    .maybeSingle();
  
  let result;
  
  if (existing) {
    // Atualiza o registro existente
    const { data, error } = await supabase
      .from("player_social_media")
      .update(updates)
      .eq("profile_id", userId)
      .select();
      
    if (error) throw error;
    result = data;
  } else {
    // Cria um novo registro
    const { data, error } = await supabase
      .from("player_social_media")
      .insert({ profile_id: userId, ...updates })
      .select();
      
    if (error) throw error;
    result = data;
  }
  
  return result;
};

// Função para upload de imagem (perfil ou banner)
export const uploadProfileImage = async (
  userId: string, 
  file: File, 
  imageType: 'profile' | 'banner'
) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/${imageType}-${uuidv4()}.${fileExt}`;
  
  // Upload da imagem
  const { error: uploadError } = await supabase.storage
    .from('profile-images')
    .upload(fileName, file);
    
  if (uploadError) throw uploadError;
  
  // Gera URL pública
  const { data } = supabase.storage
    .from('profile-images')
    .getPublicUrl(fileName);
    
  // Atualiza o URL no mentor_info ou profile dependendo do tipo
  if (imageType === 'profile') {
    await updateMentorData(userId, { image_url: data.publicUrl });
  } else if (imageType === 'banner') {
    await updateMentorData(userId, { cover_url: data.publicUrl });
  }
  
  return data.publicUrl;
};

// Função para remover imagens
export const removeProfileImage = async (
  userId: string,
  imageType: 'profile' | 'banner'
) => {
  // Atualiza para NULL no banco, dependendo do tipo
  if (imageType === 'profile') {
    await updateMentorData(userId, { image_url: null });
  } else if (imageType === 'banner') {
    await updateMentorData(userId, { cover_url: null });
  }
  
  // Não estamos excluindo o arquivo do storage para evitar problemas
  // se o mesmo arquivo estiver sendo usado em outro lugar
  
  return true;
};
